import { memo, useCallback, useMemo } from 'react';
import { Field, useField } from 'formik';
import {
    BaseTextFieldProps,
    Box,
    InputAdornment,
    Stack,
    SxProps,
    TextField,
    Theme,
    Tooltip,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlined';
import ReplayIcon from '@mui/icons-material/Replay';

import {
    InterviewLanguage,
    PuppetQuestion,
    UploadFileInfo,
} from '@synergetx/puppet-interview-types';

import AudioUploadIconButton from './AudioUploadIconButton';
import AudioPlayer from '@components/AudioPlayer';
import theme from '@modules/theme';

// Outlined icons for softer visual appearance
const PLAYER_ICONS = {
    PlayIcon: PlayCircleOutlineIcon,
    PauseIcon: PauseCircleOutlineIcon,
    ReplayIcon: ReplayIcon,
};

// Style to apply info (blue) color to player icons
const playerIconStyle = {
    '& .MuiSvgIcon-root': {
        color: theme.palette.info.main,
    },
};

interface Props {
    position: number;
    language: InterviewLanguage;
}

const PuppetQuestionFields = ({ position, language }: Props) => {
    const [{ value: question }, , { setValue: setQuestion }] = useField<PuppetQuestion>(
        `questions[${position}]`,
    );

    const fieldInputProps: BaseTextFieldProps = useMemo(
        () => FIELD_INPUT_PROPS,
        // Removing empty dependency array since FIELD_INPUT_PROPS is constant
        [],
    );

    const questionName = question?.name || 'unknown';

    const path = useCallback(
        (languageUsed: string, direction: 'left' | 'right') =>
            `${questionName}/PuppetAudio/${languageUsed}/${direction}_${Date.now()}`,
        [questionName],
    );

    const onUpdateAudio = useCallback(
        (key: 'leftAudioAsset' | 'rightAudioAsset', fileInfo: UploadFileInfo) => {
            setQuestion({
                ...question,
                localeContent: {
                    ...question.localeContent,
                    [language]: {
                        ...question.localeContent?.[language],
                        [key]: fileInfo,
                    },
                },
            });
        },
        [question, setQuestion, language],
    );

    const leftAudioUrl = question.localeContent?.[language]?.leftAudioAsset?.url;
    const rightAudioUrl = question.localeContent?.[language]?.rightAudioAsset?.url;

    return (
        <Stack
            key={language}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
                mb: 1,
            }}
        >
            <AudioUploadIconButton
                style={{ display: 'none' }}
                path={path(language, 'left')}
                onUploadFinish={(fileInfo) => onUpdateAudio('leftAudioAsset', fileInfo)}
                fileUploadedPath={leftAudioUrl || false}
            />
            <AudioUploadIconButton
                style={{ display: 'none' }}
                path={path(language, 'right')}
                onUploadFinish={(fileInfo) => onUpdateAudio('rightAudioAsset', fileInfo)}
                fileUploadedPath={rightAudioUrl || false}
            />

            {/* Left puppet input with inline controls */}
            <Field
                as={TextField}
                fullWidth
                name={`questions[${position}]['localeContent'][${language}].leftText`}
                label={`Left Puppet (${language})`}
                variant="outlined"
                placeholder="What does the left puppet say?"
                slotProps={{
                    input: {
                        ...fieldInputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {leftAudioUrl && (
                                        <Tooltip title="Play audio">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    '& .MuiGrid-container': {
                                                        width: 'auto',
                                                        flexWrap: 'nowrap',
                                                    },
                                                    ...playerIconStyle,
                                                }}
                                            >
                                                <AudioPlayer
                                                    spacing={0}
                                                    width="24px"
                                                    height="24px"
                                                    debug={false}
                                                    src={leftAudioUrl}
                                                    icons={PLAYER_ICONS}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <AudioUploadIconButton
                                        path={path(language, 'left')}
                                        onUploadFinish={(fileInfo) =>
                                            onUpdateAudio('leftAudioAsset', fileInfo)
                                        }
                                        fileUploadedPath={leftAudioUrl || false}
                                        style={inputButtonStyle}
                                    />
                                </Box>
                            </InputAdornment>
                        ),
                    },
                    inputLabel: { shrink: true },
                }}
            />

            {/* Right puppet input with inline controls */}
            <Field
                as={TextField}
                fullWidth
                name={`questions[${position}].localeContent.[${language}].rightText`}
                label={`Right Puppet (${language})`}
                variant="outlined"
                placeholder="What does the right puppet say?"
                slotProps={{
                    input: {
                        ...fieldInputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {rightAudioUrl && (
                                        <Tooltip title="Play audio">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    '& .MuiGrid-container': {
                                                        width: 'auto',
                                                        flexWrap: 'nowrap',
                                                    },
                                                    ...playerIconStyle,
                                                }}
                                            >
                                                <AudioPlayer
                                                    spacing={0}
                                                    width="24px"
                                                    height="24px"
                                                    debug={false}
                                                    src={rightAudioUrl}
                                                    icons={PLAYER_ICONS}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <AudioUploadIconButton
                                        path={path(language, 'right')}
                                        onUploadFinish={(fileInfo) =>
                                            onUpdateAudio('rightAudioAsset', fileInfo)
                                        }
                                        fileUploadedPath={rightAudioUrl || false}
                                        style={inputButtonStyle}
                                    />
                                </Box>
                            </InputAdornment>
                        ),
                    },
                    inputLabel: { shrink: true },
                }}
            />
        </Stack>
    );
};

const inputButtonStyle: SxProps<Theme> = {
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.grey[300],
        borderRadius: 5,
    },
};

// Optimize: Move fieldInputProps outside component to prevent recreation
const FIELD_INPUT_PROPS: BaseTextFieldProps = {
    style: { minWidth: '15rem' },
    onFocus: (e) => {
        e.target.select();
    },
    onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
    },
};

// Memoize props comparison for better re-render control
export default memo(PuppetQuestionFields, (prevProps, nextProps) => {
    return prevProps.position === nextProps.position && prevProps.language === nextProps.language;
});
